'use client';

import type { ReactNode } from 'react';
import { BlockErrorBoundary } from './BlockErrorBoundary';
import { RenderOneBlock } from './BlockRenderer';
import { ThemeContext } from './contexts';
import type { BlockInstance } from './types';

/**
 * Renders a flat array of {@link BlockInstance} using the standard
 * portal block pipeline (theme-aware {@code resolveBlock},
 * localization via the active {@link LocaleContext}, per-block error
 * boundary). The full {@link BlockRenderer} takes a template + slots;
 * this is the simpler "I just have a list of blocks, render them"
 * surface used by:
 *
 * <ul>
 *   <li>Host-supplied {@code BlogDetailLayout} / {@code WikiDetailLayout}
 *       for placement zones (top, bottom, sidebar, inline-after-N)</li>
 *   <li>{@link ArticleTopBlocks} / {@link ArticleBottomBlocks} /
 *       {@link ArticleSidebarBlocks} helpers used by themed layouts</li>
 *   <li>Any surface that needs to render an arbitrary block list</li>
 * </ul>
 *
 * <h3>ThemeContext threading</h3>
 *
 * Detail layouts often render OUTSIDE a {@link BlockRenderer} (an
 * article body, not a page composition), so the outer
 * {@link ThemeContext} would be at its default {@code null}. Inner
 * blocks would then resolve against the default theme even though the
 * host has an active theme. Callers must pass {@code themeId} so the
 * provider sets it correctly for every block inside.
 *
 * <p>When this list IS used inside a {@link BlockRenderer} (rare but
 * possible), the outer context already has the right value — and
 * callers pass the same themeId through props, so the inner provider
 * sets the same value. No behavioral change for the existing-correct
 * path.
 *
 * <p>{@link LocaleContext} and {@link AuthContext} are NOT re-provided
 * here — those have sensible defaults baked into {@code createContext},
 * and detail-layout use cases don't depend on either being non-default.
 */
export function BlockList({
  items,
  themeId,
}: {
  items: BlockInstance[];
  /** Inherited from {@link BlockRenderer}'s {@link ThemeContext} when
   *  this list is rendered inside a layout that has one. On article /
   *  wiki detail paths there is NO outer {@link BlockRenderer}, so the
   *  caller MUST pass the value through. The provider below makes the
   *  value available to inner blocks via {@code useThemeId()}. */
  themeId?: string | null;
}): ReactNode {
  if (!items || items.length === 0) return null;
  return (
    <ThemeContext.Provider value={themeId ?? null}>
      {items.map((b, i) => (
        <BlockErrorBoundary key={`block-${i}`} blockType={b.type}>
          <RenderOneBlock instance={b} />
        </BlockErrorBoundary>
      ))}
    </ThemeContext.Provider>
  );
}
